import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Alert,
  TablePagination,
  Autocomplete,
  List,
  ListItemButton,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Visibility,
  PictureAsPdf,
  Email,
  Comment,
  AddCircleOutline,
  RemoveCircleOutline,
  PlaylistAdd,
  ArrowBack,
  Close,
} from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  CAP_CLASS_OPTIONS,
  HOLDING_TYPE_OPTIONS,
  PLAN_STATUS_OPTIONS,
  STOCK_DOMAIN_OPTIONS,
  StockPickCapClass,
  StockPlan,
  StockPlanExternalLink,
  StockPlanStatus,
  StockProfile,
  capClassLabel,
  emptyPlan,
  emptyProfile,
  holdingTypeLabel,
  newExternalLink,
  planDisplayTitle,
  planStatusChipColor,
  planStatusLabel,
  stockProfileDisplayName,
} from '../../../../models/StockPickTypes';
import { getNseCapTier } from '../../../../utils/nseCapTier';
import {
  addStockPlanComment,
  createStockPlan,
  createStockProfileWithPlan,
  deleteStockPlan,
  deleteStockPlanComment,
  deleteStockProfile,
  getStockPlan,
  getStockProfile,
  getStockProfilesPage,
  updateStockPlan,
  updateStockProfile,
} from '../../../../services/request/StockPick';
import { formatCurrency } from '../../../../utils/CurrencyFormat';
import { formatDateForPdf } from '../../../../utils/pdfFormat';
import { getPlanExecution } from '../../../../services/request/planExecutionApi';
import { exportStockPlanPdf } from '../../../../utils/stockPickPdf';
import { PaginationConst } from '../../../../services/constants/AppConstant';
import PlanCapitalAllocationsPanel from './PlanCapitalAllocationsPanel';
import PlanExecutionPanel from './plan-execution/PlanExecutionPanel';
import PlanStatusStepper from './PlanStatusStepper';
import PlanReportDialog from './PlanReportDialog';

function PlanTabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

type ProfileFormMode = 'create-with-plan' | 'edit-profile';
type RightPaneMode = 'empty' | 'view' | 'edit' | 'create';

function renderPlanLinksEditor(
  form: StockPlan,
  onUrlChange: (index: number, url: string) => void,
  onLabelChange: (index: number, label: string) => void,
  onAdd: () => void,
  onRemove: (index: number) => void,
) {
  return (
    <>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2">External reference links</Typography>
        <Button size="small" startIcon={<AddCircleOutline />} onClick={onAdd}>
          Add link
        </Button>
      </Box>
      {(form.externalLinks ?? []).map((link, idx) => (
        <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField label="URL" value={link.url} onChange={(e) => onUrlChange(idx, e.target.value)} fullWidth size="small" />
          <TextField label="Comment / label" value={link.label ?? ''} onChange={(e) => onLabelChange(idx, e.target.value)} fullWidth size="small" />
          <IconButton color="error" onClick={() => onRemove(idx)} disabled={(form.externalLinks?.length ?? 0) <= 1} sx={{ mt: { sm: 0.5 } }}>
            <RemoveCircleOutline />
          </IconButton>
        </Stack>
      ))}
    </>
  );
}

const StockPickHub: React.FC = () => {
  const [rows, setRows] = useState<StockProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [profileFormOpen, setProfileFormOpen] = useState(false);
  const [profileFormMode, setProfileFormMode] = useState<ProfileFormMode>('create-with-plan');
  const [profileForm, setProfileForm] = useState<StockProfile>(emptyProfile());
  const [firstPlanForm, setFirstPlanForm] = useState<StockPlan>(emptyPlan());
  const [profileSaving, setProfileSaving] = useState(false);

  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [activeProfile, setActiveProfile] = useState<StockProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [rightPaneMode, setRightPaneMode] = useState<RightPaneMode>('empty');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState<StockPlan | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [planForm, setPlanForm] = useState<StockPlan>(emptyPlan());
  const [planPaneLoading, setPlanPaneLoading] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [planViewTab, setPlanViewTab] = useState(0);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getStockProfilesPage(page, PaginationConst.PAGE_SIZE);
      setRows(data.result ?? []);
      setTotal(data.totalCount ?? 0);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load stock profiles');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const clearRightPane = () => {
    setRightPaneMode('empty');
    setSelectedPlanId(null);
    setActivePlan(null);
    setPlanForm(emptyPlan());
    setPlanViewTab(0);
    setNewComment('');
  };

  const loadProfile = async (id: string) => {
    setProfileLoading(true);
    try {
      const profile = await getStockProfile(id);
      setActiveProfile(profile);
      setSelectedProfileId(id);
      return profile;
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load profile');
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const refreshActiveProfile = async (id: string) => {
    const profile = await loadProfile(id);
    loadList();
    return profile;
  };

  const openCreateProfile = () => {
    setProfileFormMode('create-with-plan');
    setProfileForm(emptyProfile());
    setFirstPlanForm(emptyPlan());
    setProfileFormOpen(true);
  };

  const openEditProfile = (profile: StockProfile) => {
    setProfileFormMode('edit-profile');
    setProfileForm({ ...profile });
    setProfileFormOpen(true);
  };

  const selectProfile = async (id: string) => {
    clearRightPane();
    await loadProfile(id);
  };

  const backToProfiles = () => {
    setSelectedProfileId(null);
    setActiveProfile(null);
    clearRightPane();
  };

  const reloadActivePlan = async () => {
    if (!activePlan?.id) return;
    const refreshed = await getStockPlan(activePlan.id);
    setActivePlan(refreshed);
  };

  const handlePlanStatusChanged = async (status: StockPlanStatus) => {
    if (!activePlan) return;
    setActivePlan({ ...activePlan, planStatus: status });
    if (selectedProfileId) {
      await loadProfile(selectedProfileId);
    }
  };

  const openPlanView = async (planId: string) => {
    setPlanPaneLoading(true);
    setSelectedPlanId(planId);
    setRightPaneMode('view');
    setPlanViewTab(0);
    try {
      const plan = await getStockPlan(planId);
      setActivePlan(plan);
      setNewComment('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load plan');
      clearRightPane();
    } finally {
      setPlanPaneLoading(false);
    }
  };

  const openPlanEdit = async (planId: string) => {
    setPlanPaneLoading(true);
    setSelectedPlanId(planId);
    setRightPaneMode('edit');
    try {
      const plan = await getStockPlan(planId);
      setPlanForm({
        ...plan,
        externalLinks: plan.externalLinks?.length ? plan.externalLinks : [newExternalLink()],
      });
      setActivePlan(plan);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load plan');
      clearRightPane();
    } finally {
      setPlanPaneLoading(false);
    }
  };

  const openPlanCreate = () => {
    if (!activeProfile?.id) return;
    setSelectedPlanId(null);
    setActivePlan(null);
    setPlanForm({ ...emptyPlan(), stockProfileId: activeProfile.id });
    setRightPaneMode('create');
    setNewComment('');
  };

  const validateProfile = (profile: StockProfile): string | null => {
    if (!profile.nseCode?.trim() && !profile.bseCode?.trim()) {
      return 'At least one of NSE or BSE code is required';
    }
    return null;
  };

  const validatePlan = (plan: StockPlan): string | null => {
    if (!plan.planAndReasons?.trim()) return 'Plan and reasons are required';
    return null;
  };

  const handleSaveProfile = async () => {
    const err = validateProfile(profileForm);
    if (err) {
      toast.error(err);
      return;
    }
    setProfileSaving(true);
    try {
      const payload: StockProfile = {
        ...profileForm,
        nseCode: profileForm.nseCode?.trim().toUpperCase() || undefined,
        bseCode: profileForm.bseCode?.trim().toUpperCase() || undefined,
      };
      if (profileFormMode === 'create-with-plan') {
        const planErr = validatePlan(firstPlanForm);
        if (planErr) {
          toast.error(planErr);
          return;
        }
        const planPayload: StockPlan = {
          ...firstPlanForm,
          externalLinks: (firstPlanForm.externalLinks ?? []).filter((l) => l.url?.trim()),
        };
        const created = await createStockProfileWithPlan(payload, planPayload);
        toast.success('Stock profile and first plan created');
        setProfileFormOpen(false);
        await loadList();
        if (created.id) {
          await selectProfile(created.id);
          const firstPlan = created.plans?.[0];
          if (firstPlan?.id) {
            await openPlanView(firstPlan.id);
          }
        }
      } else if (profileForm.id) {
        await updateStockProfile(profileForm.id, payload);
        toast.success('Profile updated');
        setProfileFormOpen(false);
        if (selectedProfileId === profileForm.id) {
          await refreshActiveProfile(profileForm.id);
        }
        loadList();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDeleteProfile = async (id: string) => {
    if (!window.confirm('Delete this stock profile and all its plans?')) return;
    try {
      await deleteStockProfile(id);
      toast.success('Profile deleted');
      if (selectedProfileId === id) backToProfiles();
      loadList();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  const handleSavePlan = async () => {
    const err = validatePlan(planForm);
    if (err) {
      toast.error(err);
      return;
    }
    const profileId = planForm.stockProfileId ?? activeProfile?.id;
    if (!profileId) {
      toast.error('No stock profile selected');
      return;
    }
    setPlanSaving(true);
    try {
      const payload: StockPlan = {
        ...planForm,
        stockProfileId: profileId,
        externalLinks: (planForm.externalLinks ?? []).filter((l) => l.url?.trim()),
      };
      if (rightPaneMode === 'create') {
        const saved = await createStockPlan(profileId, payload);
        toast.success(`Plan created: ${saved.planCode}`);
        await refreshActiveProfile(profileId);
        if (saved.id) await openPlanView(saved.id);
      } else if (planForm.id) {
        const saved = await updateStockPlan(planForm.id, payload);
        toast.success('Plan updated');
        await refreshActiveProfile(profileId);
        if (saved.id) await openPlanView(saved.id);
      }
      loadList();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleDeletePlan = async (planId: string, profileId?: string) => {
    if (!window.confirm('Delete this plan and its comments?')) return;
    try {
      await deleteStockPlan(planId);
      toast.success('Plan deleted');
      if (selectedPlanId === planId) clearRightPane();
      if (profileId) await refreshActiveProfile(profileId);
      else loadList();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  const handleAddComment = async () => {
    if (!activePlan?.id || !newComment.trim()) return;
    setCommentBusy(true);
    try {
      await addStockPlanComment(activePlan.id, newComment.trim());
      const refreshed = await getStockPlan(activePlan.id);
      setActivePlan(refreshed);
      setNewComment('');
      toast.success('Comment added');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add comment');
    } finally {
      setCommentBusy(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!activePlan?.id) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteStockPlanComment(activePlan.id, commentId);
      const refreshed = await getStockPlan(activePlan.id);
      setActivePlan(refreshed);
      toast.success('Comment deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete comment');
    }
  };

  const handleNseCodeChange = (value: string) => {
    const nseCode = value.toUpperCase();
    setProfileForm((prev) => {
      const next = { ...prev, nseCode };
      if (!prev.cap && nseCode.trim()) {
        const tier = getNseCapTier(nseCode);
        if (tier === 'LARGE' || tier === 'MID' || tier === 'SMALL') {
          next.cap = tier;
        }
      }
      return next;
    });
  };

  const updateFirstPlanLink = (index: number, patch: Partial<StockPlanExternalLink>) => {
    setFirstPlanForm((prev) => {
      const links = [...(prev.externalLinks ?? [])];
      links[index] = { ...links[index], ...patch };
      return { ...prev, externalLinks: links };
    });
  };

  const addFirstPlanLinkRow = () => {
    setFirstPlanForm((prev) => ({
      ...prev,
      externalLinks: [...(prev.externalLinks ?? []), newExternalLink()],
    }));
  };

  const removeFirstPlanLinkRow = (index: number) => {
    setFirstPlanForm((prev) => {
      const links = [...(prev.externalLinks ?? [])];
      links.splice(index, 1);
      return { ...prev, externalLinks: links.length ? links : [newExternalLink()] };
    });
  };

  const updatePlanLink = (index: number, patch: Partial<StockPlanExternalLink>) => {
    setPlanForm((prev) => {
      const links = [...(prev.externalLinks ?? [])];
      links[index] = { ...links[index], ...patch };
      return { ...prev, externalLinks: links };
    });
  };

  const addPlanLinkRow = () => {
    setPlanForm((prev) => ({
      ...prev,
      externalLinks: [...(prev.externalLinks ?? []), newExternalLink()],
    }));
  };

  const removePlanLinkRow = (index: number) => {
    setPlanForm((prev) => {
      const links = [...(prev.externalLinks ?? [])];
      links.splice(index, 1);
      return { ...prev, externalLinks: links.length ? links : [newExternalLink()] };
    });
  };

  const renderRightPane = () => {
    if (planPaneLoading) {
      return (
        <Box sx={{ p: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading plan…</Typography>
        </Box>
      );
    }

    if (rightPaneMode === 'empty') {
      return (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Visibility sx={{ fontSize: 48, opacity: 0.25, mb: 2 }} />
          <Typography variant="h6" gutterBottom>Select a plan</Typography>
          <Typography variant="body2">
            {selectedProfileId
              ? 'Choose a plan from the list to view full details, or add a new iterative plan.'
              : 'Open a stock profile, then select or create a plan to see details here.'}
          </Typography>
        </Box>
      );
    }

    if (rightPaneMode === 'view' && activePlan) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{planDisplayTitle(activePlan)}</Typography>
                {activePlan.profile && (
                  <Typography variant="body2" color="text.secondary">
                    {stockProfileDisplayName(activePlan.profile)}
                    {' · '}
                    {[activePlan.profile.nseCode, activePlan.profile.bseCode].filter(Boolean).join(' / ')}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Tooltip title="Email report &amp; PDF export">
                  <IconButton onClick={() => setReportDialogOpen(true)} aria-label="Plan report">
                    <Email />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Quick PDF (admin view)">
                  <IconButton
                    onClick={() => {
                      void (async () => {
                        try {
                          const execution = activePlan.id ? await getPlanExecution(activePlan.id) : undefined;
                          exportStockPlanPdf(activePlan, { view: 'ADMIN', execution });
                        } catch {
                          exportStockPlanPdf(activePlan, { view: 'ADMIN' });
                        }
                      })();
                    }}
                  >
                    <PictureAsPdf />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit plan">
                  <IconButton onClick={() => activePlan.id && openPlanEdit(activePlan.id)}><Edit /></IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton onClick={clearRightPane}><Close /></IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip label={activePlan.planCode ?? ''} size="small" color="primary" />
              <Chip
                label={planStatusLabel(activePlan.planStatus)}
                size="small"
                color={planStatusChipColor(activePlan.planStatus)}
              />
              {activePlan.planLabel && <Chip label={activePlan.planLabel} size="small" />}
              <Chip label={holdingTypeLabel(activePlan.holdingType)} size="small" variant="outlined" />
              {activePlan.plannedCapital != null && (
                <Chip label={formatCurrency(activePlan.plannedCapital)} size="small" variant="outlined" />
              )}
              {(activePlan.totalAllocatedCapital ?? 0) > 0 && (
                <Chip label={`Allocated ${formatCurrency(activePlan.totalAllocatedCapital!)}`} size="small" color="success" variant="outlined" />
              )}
              {activePlan.profile?.domain && <Chip label={activePlan.profile.domain} size="small" variant="outlined" />}
              {activePlan.profile?.cap && <Chip label={capClassLabel(activePlan.profile.cap)} size="small" variant="outlined" />}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {activePlan.id && (
              <PlanStatusStepper
                planId={activePlan.id}
                status={activePlan.planStatus ?? 'CREATED'}
                onStatusChanged={handlePlanStatusChanged}
              />
            )}
            <Tabs
              value={planViewTab}
              onChange={(_, v) => setPlanViewTab(v)}
              sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Overview" />
              <Tab label="Partner capital" />
              <Tab label="Execution" />
              <Tab label="Notes" />
            </Tabs>

            <PlanTabPanel value={planViewTab} index={0}>
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Plan & reasons</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{activePlan.planAndReasons}</Typography>
                </Paper>
                {activePlan.exitStrategy && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Exit strategy</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{activePlan.exitStrategy}</Typography>
                  </Paper>
                )}
                {activePlan.riskMitigation && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Risk mitigation</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{activePlan.riskMitigation}</Typography>
                  </Paper>
                )}
                {(activePlan.externalLinks?.length ?? 0) > 0 && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Reference links</Typography>
                    <Stack spacing={1}>
                      {activePlan.externalLinks!.map((link) => (
                        <Typography key={link.id ?? link.url} variant="body2" component="a" href={link.url} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                          {link.label?.trim() || link.url}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                )}
                <Stack direction="row" spacing={1}>
                  <Button color="error" startIcon={<Delete />} onClick={() => handleDeletePlan(activePlan.id!, activePlan.stockProfileId)}>
                    Delete plan
                  </Button>
                </Stack>
              </Stack>
            </PlanTabPanel>

            <PlanTabPanel value={planViewTab} index={1}>
              <PlanCapitalAllocationsPanel plan={activePlan} onSaved={() => reloadActivePlan()} />
            </PlanTabPanel>

            <PlanTabPanel value={planViewTab} index={2}>
              <PlanExecutionPanel plan={activePlan} onSaved={() => reloadActivePlan()} />
            </PlanTabPanel>

            <PlanTabPanel value={planViewTab} index={3}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Comment fontSize="small" /> Timeline notes
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField placeholder="Add a note on price action, news, outcome…" value={newComment} onChange={(e) => setNewComment(e.target.value)} multiline minRows={2} fullWidth size="small" />
                  <Button variant="outlined" onClick={handleAddComment} disabled={commentBusy || !newComment.trim()} sx={{ alignSelf: 'flex-start' }}>Add</Button>
                </Stack>
                <Stack spacing={1.5}>
                  {(activePlan.comments ?? []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">No comments yet.</Typography>
                  )}
                  {(activePlan.comments ?? []).map((c) => (
                    <Box key={c.id} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{formatDateForPdf(c.createdDate)}</Typography>
                        <IconButton size="small" color="error" onClick={() => handleDeleteComment(c.id)}><Delete fontSize="inherit" /></IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{c.body}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </PlanTabPanel>
          </Box>
        </Box>
      );
    }

    if ((rightPaneMode === 'edit' || rightPaneMode === 'create') && planForm) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {rightPaneMode === 'create' ? 'New investment plan' : `Edit ${planForm.planCode ?? 'plan'}`}
              </Typography>
              <IconButton onClick={clearRightPane}><Close /></IconButton>
            </Stack>
            {activeProfile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {stockProfileDisplayName(activeProfile)}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            <Stack spacing={2}>
              {rightPaneMode === 'create' && (
                <Alert severity="info">A unique plan ID will be generated (e.g. PLAN-RELIANCE-002).</Alert>
              )}
              {planForm.planCode && (
                <TextField label="Plan ID" value={planForm.planCode} disabled fullWidth size="small" />
              )}
              <TextField label="Plan label (optional)" placeholder="e.g. 2025 H2 re-entry" value={planForm.planLabel ?? ''} onChange={(e) => setPlanForm({ ...planForm, planLabel: e.target.value })} fullWidth />
              <TextField select label="Holding type" value={planForm.holdingType} onChange={(e) => setPlanForm({ ...planForm, holdingType: e.target.value as StockPlan['holdingType'] })} fullWidth>
                {HOLDING_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              {rightPaneMode === 'edit' && (
                <TextField
                  select
                  label="Plan stage"
                  value={planForm.planStatus ?? 'CREATED'}
                  onChange={(e) => setPlanForm({ ...planForm, planStatus: e.target.value as StockPlanStatus })}
                  fullWidth
                >
                  {PLAN_STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              )}
              <TextField label="Planned capital (INR)" type="number" value={planForm.plannedCapital ?? ''} onChange={(e) => setPlanForm({ ...planForm, plannedCapital: e.target.value ? Number(e.target.value) : undefined })} fullWidth />
              <TextField label="Plan & reasons" value={planForm.planAndReasons} onChange={(e) => setPlanForm({ ...planForm, planAndReasons: e.target.value })} multiline minRows={5} required fullWidth />
              <TextField label="Exit strategy" value={planForm.exitStrategy ?? ''} onChange={(e) => setPlanForm({ ...planForm, exitStrategy: e.target.value })} multiline minRows={3} fullWidth />
              <TextField label="Risk mitigation" value={planForm.riskMitigation ?? ''} onChange={(e) => setPlanForm({ ...planForm, riskMitigation: e.target.value })} multiline minRows={3} fullWidth />
              {renderPlanLinksEditor(
                planForm,
                (idx, url) => updatePlanLink(idx, { url }),
                (idx, label) => updatePlanLink(idx, { label }),
                addPlanLinkRow,
                removePlanLinkRow,
              )}
              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button variant="contained" onClick={handleSavePlan} disabled={planSaving}>
                  {planSaving ? 'Saving…' : rightPaneMode === 'create' ? 'Create plan' : 'Save changes'}
                </Button>
                <Button onClick={() => {
                  if (rightPaneMode === 'edit' && activePlan?.id) openPlanView(activePlan.id);
                  else clearRightPane();
                }}>Cancel</Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      );
    }

    return null;
  };

  const renderLeftPane = () => {
    if (selectedProfileId && activeProfile) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Button size="small" startIcon={<ArrowBack />} onClick={backToProfiles} sx={{ mb: 1 }}>
              All profiles
            </Button>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{stockProfileDisplayName(activeProfile)}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {[activeProfile.nseCode, activeProfile.bseCode].filter(Boolean).join(' · ')}
              {activeProfile.domain ? ` · ${activeProfile.domain}` : ''}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
              {activeProfile.cap && <Chip size="small" label={capClassLabel(activeProfile.cap)} variant="outlined" />}
              <Chip size="small" label={`${activeProfile.plans?.length ?? 0} plans`} color="primary" variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" startIcon={<PlaylistAdd />} onClick={openPlanCreate}>
                Add plan
              </Button>
              <Button size="small" startIcon={<Edit />} onClick={() => openEditProfile(activeProfile)}>Edit</Button>
            </Stack>
          </Box>
          {profileLoading && <LinearProgress />}
          <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
            {(activeProfile.plans ?? []).length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">No plans yet.</Typography>
              </Box>
            )}
            {(activeProfile.plans ?? []).map((plan) => {
              const selected = selectedPlanId === plan.id;
              return (
                <ListItemButton
                  key={plan.id}
                  selected={selected}
                  onClick={() => openPlanView(plan.id)}
                  sx={{
                    borderLeft: selected ? 3 : 0,
                    borderColor: 'primary.main',
                    py: 1.5,
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{plan.planCode}</Typography>
                    }
                    secondary={
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={planStatusLabel(plan.planStatus)}
                          color={planStatusChipColor(plan.planStatus)}
                          sx={{ height: 20, fontSize: '0.65rem', alignSelf: 'flex-start' }}
                        />
                        {plan.planLabel && <Typography variant="caption">{plan.planLabel}</Typography>}
                        <Typography variant="caption" color="text.secondary">
                          {holdingTypeLabel(plan.holdingType)}
                          {plan.plannedCapital != null ? ` · ${formatCurrency(plan.plannedCapital)}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateForPdf(plan.createdDate)}</Typography>
                      </Stack>
                    }
                  />
                  <Stack direction="row" spacing={0} sx={{ ml: 1, flexShrink: 0 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openPlanEdit(plan.id); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeletePlan(plan.id, activeProfile.id); }}><Delete fontSize="small" /></IconButton>
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Stock</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell align="center">Plans</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No stock profiles yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => row.id && selectProfile(row.id)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{stockProfileDisplayName(row)}</Typography>
                    <Typography variant="caption" color="text.secondary">{[row.nseCode, row.bseCode].filter(Boolean).join(' / ')}</Typography>
                  </TableCell>
                  <TableCell>{row.domain || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={row.planCount ?? 0} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Open plans">
                      <IconButton size="small" onClick={() => row.id && selectProfile(row.id)}><Visibility fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit profile">
                      <IconButton size="small" onClick={() => openEditProfile(row)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => row.id && handleDeleteProfile(row.id)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={PaginationConst.PAGE_SIZE}
          rowsPerPageOptions={[PaginationConst.PAGE_SIZE]}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && !selectedProfileId && <LinearProgress sx={{ flexShrink: 0 }} />}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexShrink: 0, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Stock Picks</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select a profile and plan — details open in the right pane.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreateProfile}>
          New stock profile
        </Button>
      </Box>

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box
            sx={{
              width: { xs: '100%', md: 380 },
              minWidth: { md: 320 },
              maxWidth: { md: 420 },
              borderRight: { md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 280, md: 0 },
              maxHeight: { xs: 360, md: 'none' },
              flexShrink: 0,
            }}
          >
            {renderLeftPane()}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
            {renderRightPane()}
          </Box>
        </CardContent>
      </Card>

      {/* Profile create / edit — keep as dialog */}
      <Dialog open={profileFormOpen} onClose={() => setProfileFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {profileFormMode === 'create-with-plan' ? 'New stock profile + first plan' : 'Edit stock profile'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="subtitle2" color="primary">Stock identity</Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>At least one of NSE or BSE code is required.</Alert>
            <TextField label="Stock name (optional)" value={profileForm.stockName ?? ''} onChange={(e) => setProfileForm({ ...profileForm, stockName: e.target.value })} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="NSE code" value={profileForm.nseCode ?? ''} onChange={(e) => handleNseCodeChange(e.target.value)} fullWidth />
              <TextField label="BSE code" value={profileForm.bseCode ?? ''} onChange={(e) => setProfileForm({ ...profileForm, bseCode: e.target.value.toUpperCase() })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete freeSolo options={STOCK_DOMAIN_OPTIONS} value={profileForm.domain ?? ''} onChange={(_, v) => setProfileForm({ ...profileForm, domain: v ?? '' })} onInputChange={(_, v) => setProfileForm({ ...profileForm, domain: v })} renderInput={(params) => <TextField {...params} label="Domain (sector)" />} sx={{ flex: 1 }} />
              <TextField select label="Cap" value={profileForm.cap ?? ''} onChange={(e) => setProfileForm({ ...profileForm, cap: (e.target.value || undefined) as StockPickCapClass | undefined })} fullWidth>
                <MenuItem value=""><em>Not set</em></MenuItem>
                {CAP_CLASS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Stack>
            {profileFormMode === 'create-with-plan' && (
              <>
                <Divider />
                <Typography variant="subtitle2" color="primary">First investment plan</Typography>
                <TextField label="Plan label (optional)" placeholder="e.g. 2025 H1 accumulation" value={firstPlanForm.planLabel ?? ''} onChange={(e) => setFirstPlanForm({ ...firstPlanForm, planLabel: e.target.value })} fullWidth />
                <TextField select label="Holding type" value={firstPlanForm.holdingType} onChange={(e) => setFirstPlanForm({ ...firstPlanForm, holdingType: e.target.value as StockPlan['holdingType'] })} fullWidth>
                  {HOLDING_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField label="Planned capital (INR)" type="number" value={firstPlanForm.plannedCapital ?? ''} onChange={(e) => setFirstPlanForm({ ...firstPlanForm, plannedCapital: e.target.value ? Number(e.target.value) : undefined })} fullWidth />
                <TextField label="Plan & reasons" value={firstPlanForm.planAndReasons} onChange={(e) => setFirstPlanForm({ ...firstPlanForm, planAndReasons: e.target.value })} multiline minRows={4} required fullWidth />
                <TextField label="Exit strategy" value={firstPlanForm.exitStrategy ?? ''} onChange={(e) => setFirstPlanForm({ ...firstPlanForm, exitStrategy: e.target.value })} multiline minRows={2} fullWidth />
                <TextField label="Risk mitigation" value={firstPlanForm.riskMitigation ?? ''} onChange={(e) => setFirstPlanForm({ ...firstPlanForm, riskMitigation: e.target.value })} multiline minRows={2} fullWidth />
                {renderPlanLinksEditor(
                  firstPlanForm,
                  (idx, url) => updateFirstPlanLink(idx, { url }),
                  (idx, label) => updateFirstPlanLink(idx, { label }),
                  addFirstPlanLinkRow,
                  removeFirstPlanLinkRow,
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={profileSaving}>
            {profileSaving ? 'Saving…' : profileFormMode === 'create-with-plan' ? 'Create profile & plan' : 'Update profile'}
          </Button>
        </DialogActions>
      </Dialog>

      {activePlan && (
        <PlanReportDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          plan={activePlan}
        />
      )}
    </Box>
  );
};

export default StockPickHub;
